// FLC-47 canonical competition ordering. ONE comparator used everywhere
// competitions are listed (syllabus, entries, merge/split, tabulation,
// schedule, results, score sheets). Keep this identical across backend
// (helpers/competitionSort.js) + web + admin.
//
// Order (confirmed by client):
//  1. Level: First Feis, Beginner, Advanced Beginner, Novice, Prizewinner,
//     Preliminary Championships, Open Championships, Adult, Teams, Specials,
//     Non-Level, then anything else.
//  2. Graded level: dance — Reel, Light Jig, Slip Jig, Single Jig, Treble
//     Jig, Hornpipe, St. Patrick's Day, Traditional Set — then age young->old.
//     Primary dances first, then that level's second-chance dances in the same
//     dance order ("Reel 2nd Chance" sits after "Traditional Set").
//  3. Championships (Prelim, Open): age young->old (primaries, then 2nd-chance).
//  4. Adult / Teams / Specials / Non-Level / other: master syllabus order
//     (creation order = numeric code). Teams also by hand-size then age.

export const LEVEL_ORDER = [
  "FIRST FEIS",
  "BEGINNER",
  "BEGINNER 1",
  "ADVANCED BEGINNER",
  "BEGINNER 2",
  "NOVICE",
  "PRIZEWINNER",
  "PRELIMINARY CHAMPIONSHIPS",
  "OPEN CHAMPIONSHIPS",
  "ADULT",
  "TEAMS",
  "SPECIALS",
  "NON-LEVEL",
];

const GRADED_LEVELS = new Set([
  "FIRST FEIS",
  "BEGINNER",
  "BEGINNER 1",
  "ADVANCED BEGINNER",
  "BEGINNER 2",
  "NOVICE",
  "PRIZEWINNER",
]);

const CHAMP_LEVELS = new Set([
  "PRELIMINARY CHAMPIONSHIPS",
  "OPEN CHAMPIONSHIPS",
]);

const DANCE_ORDER = {
  "REEL": 1,
  "LIGHT JIG": 2,
  "SLIP JIG": 3,
  "SINGLE JIG": 4,
  "TREBLE JIG": 5,
  "HORNPIPE": 6,
  "ST. PATRICK'S DAY": 7,
  "ST PATRICKS DAY": 7,
  "TRADITIONAL SET": 8,
  "TRAD. SET": 8,
  "TRAD SET": 8,
};

const norm = (v) => String(v == null ? "" : v).trim().toUpperCase();

export function levelRank(group) {
  const i = LEVEL_ORDER.indexOf(norm(group));
  return i === -1 ? LEVEL_ORDER.length : i;
}

export function isSecondChance(comp) {
  if (comp && comp.second_chance === true) return true;
  const desc = norm(comp && comp.description);
  if (/-SECOND$/.test(desc) || /\bSECOND\b/.test(desc)) return true;
  const code = norm(comp && comp.code);
  return /-\d+$/.test(code);
}

export function danceRank(description) {
  let d = norm(description).replace(/-SECOND$/, "").trim();
  if (DANCE_ORDER[d] != null) return DANCE_ORDER[d];
  return 99;
}

export function ageRank(comp) {
  const ages = comp && Array.isArray(comp.ages) ? comp.ages : [];
  let min = Infinity;
  for (const a of ages) {
    const m = String(a).match(/\d+/);
    if (m) min = Math.min(min, parseInt(m[0], 10));
  }
  return min === Infinity ? 999 : min;
}

function teamSize(comp) {
  const code = String((comp && comp.code) || "");
  const m = code.match(/^[A-Z]+\d+-(\d+)$/);
  return m ? parseInt(m[1], 10) : 999;
}

function masterCompare(a, b) {
  const ca = parseInt(a && a.code, 10);
  const cb = parseInt(b && b.code, 10);
  const na = Number.isNaN(ca);
  const nb = Number.isNaN(cb);
  if (na && nb)
    return String((a && a.code) || "").localeCompare(String((b && b.code) || ""));
  if (na) return 1;
  if (nb) return -1;
  return ca - cb;
}

export function compareCompetitions(a, b) {
  const ra = levelRank(a && a.group);
  const rb = levelRank(b && b.group);
  if (ra !== rb) return ra - rb;

  const level = norm(a && a.group);

  if (GRADED_LEVELS.has(level)) {
    const sc = (isSecondChance(a) ? 1 : 0) - (isSecondChance(b) ? 1 : 0);
    if (sc !== 0) return sc;
    const dr = danceRank(a && a.description) - danceRank(b && b.description);
    if (dr !== 0) return dr;
    const ag = ageRank(a) - ageRank(b);
    if (ag !== 0) return ag;
    return masterCompare(a, b);
  }

  if (CHAMP_LEVELS.has(level)) {
    const sc = (isSecondChance(a) ? 1 : 0) - (isSecondChance(b) ? 1 : 0);
    if (sc !== 0) return sc;
    const ag = ageRank(a) - ageRank(b);
    if (ag !== 0) return ag;
    return masterCompare(a, b);
  }

  if (level === "TEAMS") {
    const ts = teamSize(a) - teamSize(b);
    if (ts !== 0) return ts;
    const ag = ageRank(a) - ageRank(b);
    if (ag !== 0) return ag;
    return masterCompare(a, b);
  }

  return masterCompare(a, b);
}

// Sort a flat list of competitions (does not mutate the input).
export function sortCompetitionsFormula(competitions) {
  if (!Array.isArray(competitions)) return competitions;
  return competitions.slice().sort(compareCompetitions);
}

// Reorder a { levelName: [...] } object's keys by the canonical level order.
export function sortLevelKeys(levelData) {
  const out = {};
  Object.keys(levelData || {})
    .sort((a, b) => levelRank(a) - levelRank(b))
    .forEach((k) => {
      out[k] = levelData[k];
    });
  return out;
}
